import React, { useContext } from 'react';
import { SiteContext } from 'src/context/site-context';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { flex } from 'src/styling/constants';
import HostedImage from 'src/components/common/hosted-image';
import mq from 'src/styling/media-queries';
import type { Styling } from 'src/context/styling';
import type { BuildType } from 'src/enums/build/build-type';
import { getFooterColors } from 'src/components/layout/footer/redesign/colors';
import type { CSSInterpolation } from '@emotion/serialize';

type SocialMediaSectionProps = {
  title: string | null;
  images: readonly Queries.FooterImage[];
  footerImageHeight: string[];
};

const createComponentStyling = (styling: Styling, buildType: BuildType) => {
  const footerColors = getFooterColors(buildType);

  return {
    sectionTitle: [
      styling.fonts.bold,
      mq({
        color: footerColors.heading,
        fontSize: '1rem',
        lineHeight: '125%',
        marginBottom: ['1rem', '2rem'],
      }),
    ],
    imageLink: {
      display: 'inline-block',
    },
    socialMediaSection: {
      marginTop: '4rem',
    },
    socialMediaIcons: mq({
      columnGap: ['2.25rem', '2rem'],
      rowGap: '0.5rem',
      flexWrap: 'wrap',
    }),
  } satisfies Record<string, CSSInterpolation>;
};

const SocialMediaSection = ({ title, images, footerImageHeight }: SocialMediaSectionProps) => {
  const { pageLevel } = useContext(SiteContext);
  const { styling, buildType } = useContext(SiteContext);
  const componentStyling = createComponentStyling(styling, buildType);
  const handleClick = (name: string | null) =>
    amplitudeTrackEvent(`Click to Social Media - ${name} - Footer`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
    });

  return (
    <div css={componentStyling.socialMediaSection}>
      {title && <div css={componentStyling.sectionTitle}>{title}</div>}
      <div css={[componentStyling.socialMediaIcons, flex.row]}>
        {images.map((image) => (
          <div key={image.image.alternateText}>
            <a
              css={componentStyling.imageLink}
              href={image.link}
              target={image.openInNewTab ? '_blank' : '_self'}
              rel="noopener noreferrer"
              onClick={() => handleClick(image.image.alternateText)}
            >
              <HostedImage
                imageDetails={image.image}
                styles={mq({
                  height: footerImageHeight,
                  width: 'auto',
                })}
                loading="lazy"
              />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export { SocialMediaSection };
